import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/features/auth/authStore';

export interface NotificationItem {
  id: string;
  type?: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  priority?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
}

const FETCH_LIMIT = 30;
const POLL_INTERVAL_MS = 30000;

const formatTimeAgo = (iso: string) => {
  const created = new Date(iso);
  const now = new Date();
  const diffMinutes = Math.floor((now.getTime() - created.getTime()) / (1000 * 60));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
  return `${Math.floor(diffMinutes / 1440)}d ago`;
};

const showNotificationToast = (notification: NotificationItem) => {
  toast.custom(
    () => (
      <div className="pointer-events-auto w-80 rounded-lg shadow-lg bg-white border border-gray-200 p-4 animate-slide-in-right">
        <div className="flex items-start">
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">{notification.title}</p>
            <p className="mt-1 text-sm text-gray-600 line-clamp-3">{notification.message}</p>
            <p className="mt-2 text-xs text-gray-400">{formatTimeAgo(notification.createdAt)}</p>
          </div>
        </div>
      </div>
    ),
    { position: 'bottom-right', duration: 5000 }
  );
};

export const useNotificationFeed = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const initializedRef = useRef(false);
  const latestCreatedAtRef = useRef<string | null>(null);
  const unreadCountRef = useRef(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = useCallback(
    async (shouldToastNew = false) => {
      setLoading(true);
      try {
        const response = await api.get('/notifications', {
          params: {
            limit: FETCH_LIMIT,
            sortBy: 'createdAt',
            sortOrder: 'desc',
          },
        });

        const payload = response.data?.data;
        const fetched = (payload?.notifications || []) as NotificationItem[];
        setNotifications(fetched);

        const latestCreated = fetched[0]?.createdAt || null;
        if (!initializedRef.current) {
          initializedRef.current = true;
          latestCreatedAtRef.current = latestCreated;
        } else if (shouldToastNew && latestCreated) {
          const previousLatest = latestCreatedAtRef.current ? new Date(latestCreatedAtRef.current) : null;
          const fresh = fetched.filter((item) => {
            if (item.read) return false;
            if (!previousLatest) return true;
            return new Date(item.createdAt) > previousLatest;
          });
          latestCreatedAtRef.current = latestCreated;
          fresh.slice(0, 3).forEach(showNotificationToast);
        } else {
          latestCreatedAtRef.current = latestCreated;
        }

        if (typeof payload?.pagination?.unreadCount === 'number') {
          setUnreadCount(payload.pagination.unreadCount);
          unreadCountRef.current = payload.pagination.unreadCount;
        }
      } catch (error) {
        console.error('Failed to load notifications', error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      const count = response.data?.data?.unreadCount ?? 0;
      const previous = unreadCountRef.current;
      setUnreadCount(count);
      unreadCountRef.current = count;
      if (count > previous) {
        await fetchNotifications(true);
      }
    } catch (error) {
      console.error('Failed to load unread count', error);
    }
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      unreadCountRef.current = Math.max(0, unreadCountRef.current - 1);
    } catch (error) {
      console.error('Failed to mark notification as read', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      unreadCountRef.current = 0;
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
    }
  }, []);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      // Refresh counts after deletion to stay in sync
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to delete notification', error);
    }
  }, [fetchUnreadCount]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
  }, [fetchUnreadCount]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchNotifications(false);
    fetchUnreadCount();
    startPolling();
    return () => {
      stopPolling();
    };
  }, [fetchNotifications, fetchUnreadCount, startPolling, stopPolling]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
};
